import { useEffect, useMemo, useRef, useState } from 'react';
import './location-intro.scss';
import emitter, { EVENT_NAME_CHANGE_STATE } from '../../utils/mitt';
import gsap from 'gsap';
import { useGlobalStore } from '../../stores/use-global-store';
import clsx from 'clsx';
export default function LocationIntro() {
  const [datas] = useState([
    {
      location: '',
      name: '',
      desc: [''],
    },
    {
      location: '盛渔村海外',
      name: '仙灵岛',
      desc: [
        '置身于东海之央，上古之时，曾有桃花仙栖止其间，以仙术掘山造景，故而有仙境及世外桃源之韵味。',
        '其后灵月宫主于此筑造水月宫，并布设重重机关与荷花迷阵，岛外亦设有迷雾，外者常难得见。',
      ],
    },
    {
      location: '太湖之上',
      name: '苏州城',
      desc: [
        '立于太湖之上，三面环水，乃商贸繁盛之水乡城池，城中首要势力乃南武林盟主林家堡。',
        '阳鱼显世致使太湖水阴阳失和染毒，此为太湖水患，致城中气氛稍显紧张压抑，众人之日常生活亦颇受水患所扰。',
      ],
    },
    {
      location: '江南之隅',
      name: '杭州城',
      desc: [
        '坐落于江南之隅，为西湖环绕。城内景致如画，经济昌盛，文教兴旺，当地文士相互陶染，墨客流连群集。',
        '缘此地灵气充盈，西湖郊外隐匿诸多精怪修炼。因地劫现于尘世，山石草木受清浊失衡所扰，间或有化为石妖木魅之状。',
      ],
    },
    {
      location: '神树之畔',
      name: '蜀山',
      desc: [
        '伏羲育神树，神树骤长万丈，根系若垂天之云，深透盘古之心。盘古之心辐聚周边土石尘埃，渐次扩增，化成悬空之山，此乃“蜀山”也。',
        '蜀山周遭地气殊异，灵力至极盛，数千载前便已成修仙者会聚之所，后仙剑派于蜀山之巅引领又创立蜀山派，遂为天下仙门之首。',
      ],
    },
    {
      location: '江南东侧山中',
      name: '归云村',
      desc: [
        '归云村处于江南东侧山中，乃一隐匿尘世之地灵洞天，迄今已有数百载矣。',
        '因地劫致地脉灵气外溢，遂生就此地地灵洞天，有精、妖、兽、灵等诸般族群混居其中。因灵气资源颇丰，各族群间并无争端。',
      ],
    },
  ]);
  const [hasScroll, setHasScroll] = useState(false);
  const { camState } = useGlobalStore();
  const [dataIndex, setDataIndex] = useState(0);
  const blackOverlayRef = useRef(null);
  const textWrapperRef = useRef(null);
  const scrollWrapperRef = useRef(null);
  const locationRef = useRef(null);
  const nameRef = useRef(null);
  const divideRef = useRef(null);
  const charRefs = useRef<HTMLElement[]>([]); //</HTMLSpanElement>useRef(null);
  const shouldHide = useMemo(() => {
    return !(camState > 0 && camState <= datas.length);
  }, [camState, datas.length]);
  const hasViewFirstLocalCity = useRef(false);

  useEffect(() => {
    const showText = (index: number) => {
      new gsap.core.Timeline()
        .set(textWrapperRef.current, {
          opacity: 1,
        })
        .set([blackOverlayRef.current, scrollWrapperRef.current], {
          opacity: 0,
        })
        .addLabel('start')
        .fromTo(
          locationRef.current,
          {
            opacity: 0,
          },
          {
            opacity: 1,
            duration: 0.6,
          }
        )
        .fromTo(
          [blackOverlayRef.current, scrollWrapperRef.current],
          {
            opacity: 0,
          },
          {
            opacity: 1,
          },
          'start+=0.4'
        )
        .fromTo(
          nameRef.current,
          {
            opacity: 0,
          },
          {
            opacity: 1,
            duration: 0.6,
          },
          'start+=0.15'
        )
        .fromTo(
          divideRef.current,
          {
            width: 0,
          },
          {
            width: '100%',
            delay: 0.3,
            duration: 1,
          },
          'start'
        )
        .fromTo(
          charRefs.current,
          {
            opacity: 0,
            y: 30,
          },
          {
            opacity: 1,
            y: 0,
            stagger: 0.02,
            duration: 0.5,
          },
          'start+=0.3'
        );
    };
    const hideText = () => {
      return new Promise<void>(res => {
        new gsap.core.Timeline()
          .to(textWrapperRef.current, {
            opacity: 0,
            duration: 0.3,
            onComplete: () => {
              res();
            },
          })
          .to(
            [blackOverlayRef.current, scrollWrapperRef.current],
            {
              opacity: 0,
            },
            '<'
          );
      });
    };
    const onIndexChange = async (index: number) => {
      // console.log('onIndexChange', index);
      const dataIndex = index;
      await hideText();
      if (dataIndex < 0 || dataIndex >= datas.length) return;
      setDataIndex(dataIndex);
      if (dataIndex !== 0) {
        setTimeout(() => showText(dataIndex), 100);
      }
      if (index !== 0 && hasViewFirstLocalCity.current) {
        setHasScroll(true);
      } else if (index !== 0) {
        hasViewFirstLocalCity.current = true;
      }
    };
    emitter.on(EVENT_NAME_CHANGE_STATE, onIndexChange);
    return () => {
      emitter.off(EVENT_NAME_CHANGE_STATE, onIndexChange);
    };
  }, [datas.length]);
  const desc = () => {
    charRefs.current = [];
    return datas[dataIndex].desc.map((d, i) => (
      <p key={d + i}>
        {d.split('').map((c, ci) => (
          <>
            {/* {c === '“' && <br />} */}
            <span
              className="char"
              key={c + ci}
              ref={el => {
                el && charRefs.current.push(el);
              }}
            >
              {c}
            </span>
          </>
        ))}
      </p>
    ));
  };
  return (
    <div className={clsx(['fit_parent intro_wrapper', shouldHide && 'hide'])}>
      <div
        className="fit_parent black_overlay opacity-0"
        ref={blackOverlayRef}
      ></div>
      <div className="text_wrapper" ref={textWrapperRef}>
        <div className="title">
          <div className="location_marker" ref={locationRef}>
            <div className="marker"></div>
            <p className="glow_text">{datas[dataIndex].location}</p>
          </div>
          <div className="name glow_text" ref={nameRef}>
            {datas[dataIndex].name}
          </div>
        </div>
        <div className="divide_wrapper">
          <div className="divide fit_parent" ref={divideRef}></div>
        </div>
        <div className="desc glow_text">{desc()}</div>
      </div>
      <div
        className={clsx(
          'transition-all duration-500',
          hasScroll && 'opacity-0'
        )}
      >
        <div className="scroll_wrapper" ref={scrollWrapperRef}>
          <div className="scroll_tip"></div>
          <p className="text-[20rem]">滑动滚轮切换地图</p>
        </div>
      </div>
    </div>
  );
}
